
export type Frequency = 'daily' | 'weekly' | 'monthly';
export type AllocationMode = 'single' | 'goldSilver' | 'multi';
export type MetalKey = 'gold' | 'silver' | 'platinum' | 'palladium';

export const METAL_CODE_MAP: Record<string, MetalKey> = {
  au: 'gold',
  ag: 'silver',
  pt: 'platinum',
  pd: 'palladium',
};

export const METALS = [
  { key: 'gold', label: 'Gold' },
  { key: 'silver', label: 'Silver' },
  { key: 'platinum', label: 'Platinum' },
  { key: 'palladium', label: 'Palladium' },
] as const;


export const DEFAULT_FREQUENCY: Record<AllocationMode, Frequency> = {
  single: 'weekly',
  goldSilver: 'daily',
  multi: 'daily',
};


export type MinAmountConfig = {
  min: Record<Frequency, number>;
  default: Record<Frequency, number>;
};

export const MIN_AMOUNTS: Record<
  Exclude<AllocationMode, 'multi'>,
  MinAmountConfig
> = {
  single: {
    min: {
      daily: 10,
      weekly: 100,
      monthly: 100,
    },
    default: {
      daily: 100,
      weekly: 700,
      monthly: 3000,
    },
  },

  goldSilver: {
    min: {
      daily: 40,
      weekly: 100,
      monthly: 100,
    },
    default: {
      daily: 100,
      weekly: 700,
      monthly: 3000,
    },
  },
};


export type MultiMetalDefaults = Record<MetalKey, number> & {
  minTotalValue: number;
};

export const MULTI_DEFAULTS: Record<Frequency, MultiMetalDefaults> = {
  daily: {
    gold: 40,
    silver: 40,
    platinum: 20,
    palladium: 0,
    minTotalValue: 20,
  },

  weekly: {
    gold: 300,
    silver: 300,
    platinum: 100,
    palladium: 0,
    minTotalValue: 100,
  },

  monthly: {
    gold: 1000,
    silver: 1000,
    platinum: 1000,
    palladium: 0,
    minTotalValue: 100,
  },
};


export type SliderRule = {
  min: number;
  max: number;
  step: number;
  operationalMin?: number;
  operationalMax?: number;
};

export const SLIDER_RULES: Readonly<Record<Frequency, SliderRule>> = {
  daily: {
    min: 0,
    max: 100,
    step: 25,
    operationalMin: 25,
    operationalMax: 75,
  },

  weekly: {
    min: 0, 
    max: 100, 
    step: 5,
    operationalMin: 10,
    operationalMax: 90, 
  },

  monthly: {
    min: 0,
    max: 100,
    step: 5,
    operationalMin: 10,
    operationalMax: 90,
  },
};


export type RouteConfig = {
  allocationMode: AllocationMode;
  defaultFrequency: Frequency;
  defaultMetal?: MetalKey; 
};


export const ROUTE_CONFIG_MAP: Record<string, RouteConfig> = {
  '/sip': {
    allocationMode: 'single',
    defaultFrequency: 'weekly',
    defaultMetal: 'silver',
  },

  '/daily-saving': {
    allocationMode: 'single',
    defaultFrequency: 'daily',
    defaultMetal: 'silver',
  },

  '/multimetal-sip': {
    allocationMode: 'goldSilver',
    defaultFrequency: 'daily',
  },
};


export type PaymentMethod = 'payemandate' | 'paymanual';
export type EmandateType = 'withMandateUPI' | 'withMandate';

export interface PaymentRule {
  methods: PaymentMethod[];
  defaultMethod: PaymentMethod;
  emandateTypes?: EmandateType[];
  defaultEmandateType?: EmandateType;
}

export type PaymentRuleKey = `${AllocationMode}_${Frequency}`;

export const PAYMENT_RULES: Record<PaymentRuleKey, PaymentRule> = {
  // ---------------- DAILY ----------------
  single_daily: {
    methods: ['payemandate'],
    defaultMethod: 'payemandate',
    emandateTypes: ['withMandateUPI'],
    defaultEmandateType: 'withMandateUPI',
  },

  goldSilver_daily: {
    methods: ['payemandate'],
    defaultMethod: 'payemandate',
    emandateTypes: ['withMandateUPI'],
    defaultEmandateType: 'withMandateUPI',
  },

  multi_daily: {
    methods: ['payemandate'],
    defaultMethod: 'payemandate',
    emandateTypes: ['withMandateUPI'],
    defaultEmandateType: 'withMandateUPI',
  },

  // ---------------- WEEKLY ----------------
  single_weekly: {
    methods: ['payemandate', 'paymanual'],
    defaultMethod: 'payemandate',
    emandateTypes: ['withMandateUPI', 'withMandate'],
    defaultEmandateType: 'withMandateUPI',
  },

  goldSilver_weekly: {
    methods: ['payemandate'],
    defaultMethod: 'payemandate',
    emandateTypes: ['withMandateUPI', 'withMandate'],
    defaultEmandateType: 'withMandateUPI',
  },

  multi_weekly: {
    methods: ['payemandate'],
    defaultMethod: 'payemandate',
    emandateTypes: ['withMandateUPI', 'withMandate'],
    defaultEmandateType: 'withMandateUPI',
  },

  // ---------------- MONTHLY ----------------
  single_monthly: {
    methods: ['payemandate', 'paymanual'],
    defaultMethod: 'payemandate',
    emandateTypes: ['withMandateUPI', 'withMandate'],
    defaultEmandateType: 'withMandateUPI',
  },

  goldSilver_monthly: {
    methods: ['payemandate'],
    defaultMethod: 'payemandate',
    emandateTypes: ['withMandateUPI', 'withMandate'],
    defaultEmandateType: 'withMandateUPI',
  },

  multi_monthly: {
    methods: ['payemandate'],
    defaultMethod: 'payemandate',
    emandateTypes: ['withMandateUPI', 'withMandate'],
    defaultEmandateType: 'withMandateUPI',
  },
};


export const FAQS = [
  {
    question: "What is SIP?",
    answer:
      "The full form of SIP is Systematic Investment Plan. In this scheme, you can buy precious metals (gold, silver, platinum, and palladium) for a fixed amount at a fixed interval (weekly or monthly). It enables you to accumulate precious metals in smaller quantities through periodic investments. The installment amount is used on your selected date to buy metals at the closing price of 10 a.m. on the same day."
  },
  {
    question: "Can you choose multiple SIP?",
    answer:
      "Yes, you can choose more than one SIP by adding individual SIP details for each."
  },
  {
    question: "What is the purity of the precious metals offered to customers?",
    answer:
      "eBullion sources precious metals from London Good Delivery Refiners only. The minimum purity requirements are: Gold – 9999, Platinum – 9995, Silver – 9999, Palladium – 9995."
  },
  {
    question: "What is the minimum amount of investment for SIP?",
    answer:
      "The SIP investment can be started with a minimum amount of Rs. 100 per week or month. There is no maximum investment limit, and you can invest in multiples of Rs. 100."
  },
  {
    question: "Can the tenure of the SIP plan be extended?",
    answer:
      "No, once a SIP plan is created, its tenure cannot be extended. However, you can create a new SIP plan with one click after the current SIP expires."
  },
  {
    question: "Where can I find the invoice for each order?",
    answer:
      "You can download the invoice from the Passbook section on the dashboard."
  },
  {
    question: "Is it necessary to create multiple e-mandates for multiple SIPs?",
    answer:
      "Yes, it is mandatory to create a separate e-mandate for each new SIP."
  }
];


